import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MODAL_SIZES, ORDER_METHODS, REDUX_STORE_SLICES, SCREENS } from '../../services/exports/Constants';
import CustomModal from '../../components-library/CustomModal';
import useScreenType from '../../hooks/utility/useScreenType';
import useAppState from '../../hooks/global/useAppState';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import CloseCircleIcon from '../../assets/icons/CloseCircleIcon';
import OrderMethodsTabBar from '../../components-library/navigation/OrderMethodsTabBar';
import CompaniesList from './CompaniesList';
import CustomButton from '../../components-library/buttons/CustomButton';
import LocationDataProvider, { useLocationData } from './provider/LocationDataProvider';
import { selectCompany } from '../../store/slices/InitialDataSlice';
import { useTranslation } from 'react-i18next';
import { useGetFranchiseDataQuery } from '../../store/api-slices';
import useDeliveryCompatibleStuff from './useDeliveryCompatibleStuff';
import { useFormContext } from 'react-hook-form';
import ScheduledOrderModal from './ScheduledOrderModal';
import useRouter from '../../hooks/navigation/useRouter';
import useStore from '../../hooks/store/useStore';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import collect from 'collect.js';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import BackIcon from '../../assets/logo/BackIcon';
import Theme from '../../components-library/Theme';

export enum ValidationErrors {
  address_not_found = 'address_not_found',
  address_not_serviceable = 'address_not_serviceable',
  no_drivers_available = 'no_drivers_available',
}

export default function OrderMethodModal() {
  const { isScheduledModalOpen, company } = useAppState().orderMethodModalProps;

  const ScheduleOrderModal = useMemo(
    () => isScheduledModalOpen && <ScheduledOrderModal company={company} className="absolute inset-0" />,
    [isScheduledModalOpen, company],
  );

  return (
    <Wrapper>
      <LocationDataProvider>
        <Content />
        {ScheduleOrderModal}
      </LocationDataProvider>
    </Wrapper>
  );
}

function Wrapper(props: PropsWithChildren) {
  const { children } = props;
  const { openModal } = useAppState().orderMethodModalProps;
  const { isMobileApp } = useMobileApp();
  const { isDesktop } = useScreenType();
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const hasLiveOrders = useSelector(selectCompany)?.has_live_orders;
  const isModalClosable = isOrderMethodInfoComplete || !hasLiveOrders;
  const { toggleOrderMethodModal } = useAppMethods();

  const toggleModal = (): void => {
    toggleOrderMethodModal({ openModal: false });
  };

  if (isMobileApp) {
    return <>{children}</>;
  }

  return (
    <CustomModal
      open={openModal}
      size={MODAL_SIZES.SM}
      fullScreen={!isDesktop}
      onClose={isModalClosable ? toggleModal : null}
      containerClassName="h-[90dvh] relative"
    >
      {children}
    </CustomModal>
  );
}

const Content = () => {
  const orderMethodModalProps = useAppState().orderMethodModalProps;
  const { orderMethod: propsOrderMethod } = orderMethodModalProps;
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const defaultCompany = useSelector(selectCompany);
  const { franchise } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const hasLiveOrders = useSelector(selectCompany)?.has_live_orders;
  const isModalClosable = isOrderMethodInfoComplete || !hasLiveOrders;
  const deliveryAddressForm = useFormContext();
  const { isValid } = deliveryAddressForm.formState;
  const { orderMethod, setOrderMethod, save, loading, company, setCompany, scheduledFor } = useLocationData();
  const { orderingDisabled, hasAsapOrders, hasScheduledOrders } = useOrderMethodsStatus(company);
  const { toggleOrderMethodModal } = useAppMethods();
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });
  const { navigate } = useRouter();
  const [loadingNavigation, setLoadingNavigation] = useState(false);
  const isCheckoutScreen = window.location.pathname.includes(SCREENS.CHECKOUT);
  const locationSwitchDisabled = isCheckoutScreen;

  const [addressSearch, setAddressSearch] = useState('');

  function toggleModal(): void {
    toggleOrderMethodModal({ openModal: false });
  }

  const { data: franchiseData, isLoading } = useGetFranchiseDataQuery(false, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    selectFromResult: (res) => {
      const companies = collect(res.data?.data.companies).filter((company) => company?.has_live_orders);

      return {
        ...res,
        data: {
          ...res.data,
          companies,
        },
      };
    },
  });

  const data = useMemo(() => {
    return {
      ...franchiseData,
      data: {
        ...franchiseData.data,
        companies:
          locationSwitchDisabled && franchiseData?.data?.companies?.length > 0
            ? [defaultCompany]
            : franchiseData?.data?.companies,
      },
    };
  }, [defaultCompany, franchiseData, locationSwitchDisabled]);

  const {
    handle,
    queryState,
    isLoading: isDeliveryStuffLoading,
    validationError,
    resetError,
  } = useDeliveryCompatibleStuff();

  useEffect(() => {
    const companies = data?.data?.companies;

    if (!companies?.length || companies?.length > 1 || !!company) {
      return;
    }

    setCompany(companies[0]);
  }, [data?.data?.companies, company]);

  useEffect(() => {
    if (!propsOrderMethod) {
      return;
    }

    setOrderMethod(propsOrderMethod);
  }, []);

  const hasOnSiteTab = data?.data?.companies?.some((company) => company?.has_room_service);
  const forcedOrderMethods = hasOnSiteTab ? [ORDER_METHODS.ROOM_SERVICE] : [];
  const saveButtonTitle = useMemo(() => {
    if (orderMethod !== ORDER_METHODS.DELIVERY) {
      return isCheckoutScreen ? t('buttons.save') : t('buttons.view_menu');
    }

    if (queryState.isUninitialized) {
      return t('buttons.next');
    }

    return isCheckoutScreen ? t('buttons.save') : t('buttons.view_menu');
  }, [orderMethod, queryState.isUninitialized, isCheckoutScreen]);

  const onCustomBtnClick = () => {
    if (orderMethod !== ORDER_METHODS.DELIVERY || !queryState.isUninitialized) {
      setLoadingNavigation(true);
      return setTimeout(() => {
        save();
        setLoadingNavigation(false);
      }, 500);
    }

    const {
      delivery_street_name: street_name,
      delivery_street_number: street_number,
      delivery_city: city,
      delivery_zip_code: zip_code,
    } = deliveryAddressForm.getValues();

    setAddressSearch(
      AddressHelpers.setCountry(company?.country).format({
        street_name,
        street_number,
        city,
        zip_code,
      }),
    );
    return handle();
  };

  const header = useMemo(() => {
    if (locationSwitchDisabled && franchise?.has_multiple_companies) {
      return (
        <label
          onClick={() => {
            toggleModal();
            navigate({
              web: {
                to: SCREENS.HOME_SCREEN,
              },
            });
          }}
          className={
            'w-fit flex flex-col items-center justify-center py-[6px] px-[6px] rounded-[4px] bg-[#FDF3BB] cursor-pointer'
          }
        >
          <p className="text-sm">{t('labels.change_location')}</p>
        </label>
      );
    }
    if (orderMethodModalProps?.showReturn) {
      return (
        <div
          className={
            'rounded-full shadow-icon cursor-pointer dark:shadow-none w-[28px] h-[28px] flex items-center justify-center bg-brand-icon-background-primary border border-brand-border'
          }
          onClick={() => {
            toggleModal();
            navigate({
              web: {
                to: SCREENS.HOME_SCREEN,
              },
            });
          }}
        >
          <BackIcon className="h-3 !w-3" color={Theme.color.black.DEFAULT} />
        </div>
      );
    }

    return <h3 className="font-medium">{t('title')}</h3>;
  }, [isCheckoutScreen, orderMethodModalProps?.showReturn]);

  const isCustomBtnLoading = loading || isLoading || isDeliveryStuffLoading || loadingNavigation;

  const isCustomBtnDisabled = useMemo(() => {
    const isDisabled =
      isLoading ||
      loading ||
      isDeliveryStuffLoading ||
      (orderMethod === ORDER_METHODS.PICKUP &&
        !hasAsapOrders(orderMethod) &&
        hasScheduledOrders(orderMethod) &&
        !scheduledFor) ||
      (orderMethod === ORDER_METHODS.DELIVERY &&
        !hasAsapOrders(orderMethod) &&
        hasScheduledOrders(orderMethod) &&
        !scheduledFor &&
        !queryState.isUninitialized &&
        collect(queryState.data).isNotEmpty()) ||
      (orderMethod !== ORDER_METHODS.DELIVERY && !company) ||
      (orderMethod === ORDER_METHODS.DELIVERY && !queryState.isUninitialized && !company) ||
      (orderMethod === ORDER_METHODS.DELIVERY &&
        !queryState.isUninitialized &&
        !queryState.data?.includes(company?.id)) ||
      (orderMethod === ORDER_METHODS.DELIVERY && queryState.isUninitialized && !isValid) ||
      (orderMethod === ORDER_METHODS.DELIVERY && !queryState.isUninitialized && !queryState.data?.length) ||
      (orderMethod === ORDER_METHODS.ROOM_SERVICE && !company?.has_room_service);

    if (orderingDisabled(orderMethod)) {
      return false;
    }

    return isDisabled;
  }, [
    isLoading,
    loading,
    isDeliveryStuffLoading,
    orderMethod,
    company,
    scheduledFor,
    queryState.isUninitialized,
    isValid,
  ]);
  const onOrderMethodChange = (orderMethod: ORDER_METHODS) => {
    validationError && resetError();
    setOrderMethod(orderMethod);
  };

  return (
    <div className="w-full pb-md lg:pb-lg px-md lg:px-lg bg-brand-background-primary space-y-md flex flex-col h-full">
      <div className="bg-brand-background-primary pt-md lg:pt-lg space-y-md">
        <div className="flex justify-between items-center gap-sm">
          {header}
          {isModalClosable && (
            <button onClick={toggleModal}>
              <CloseCircleIcon />
            </button>
          )}
        </div>
        <OrderMethodsTabBar
          orderMethod={orderMethod}
          onClick={onOrderMethodChange}
          className="bg-gray dark:bg-brand-background-secondary"
          company={company}
          forcedOrderMethods={forcedOrderMethods}
        />
      </div>
      <CompaniesList
        data={data?.data}
        addressSearch={addressSearch}
        setAddressSearch={setAddressSearch}
        locationSwitchDisabled={locationSwitchDisabled}
        className="h-full overflow-y-auto"
        validationError={validationError}
      />
      <CustomButton
        title={saveButtonTitle}
        onClick={onCustomBtnClick}
        loading={isCustomBtnLoading}
        disabled={isCustomBtnDisabled}
      />
    </div>
  );
};
